// =============================================================================
// VandieZ shipper-web -- Dispute detail (/disputes/:tripId)
// =============================================================================
// Lists every dispute on the trip + offers a "File new dispute" form.
// One open dispute per trip is the backend constraint -- if there's
// already an open one, the file-new form hides itself.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Plus,
  Scale,
  Send,
  XCircle,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { disputesApi, tripsApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  DisputeCategory,
  DisputeRead,
  DisputeStatus,
} from "@/types/dispute";
import type { Trip } from "@/types/trip";

const CATEGORIES: { value: DisputeCategory; label: string }[] = [
  { value: "damage", label: "Damaged goods" },
  { value: "missing_goods", label: "Missing goods" },
  { value: "wrong_amount", label: "Wrong amount charged" },
  { value: "late_delivery", label: "Late delivery" },
  { value: "no_show", label: "Driver no-show" },
  { value: "other", label: "Other" },
];

const STATUS_LABEL: Record<DisputeStatus, string> = {
  open: "Open",
  under_review: "Under review",
  resolved: "Resolved",
  rejected: "Rejected",
};

const STATUS_TONE: Record<DisputeStatus, string> = {
  open: "bg-warning-subtle text-warning-subtle-foreground",
  under_review: "bg-primary-subtle text-primary-subtle-foreground",
  resolved: "bg-success-subtle text-success-subtle-foreground",
  rejected: "bg-muted text-muted-foreground",
};

function formatDate(iso: string | null): string {
  if (iso === null) {return "—";}
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function DisputeDetailPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const { tripId } = useParams<{ tripId: string }>();
  const toast = useToast();

  const [trip, setTrip] = useState<Trip | null>(null);
  const [disputes, setDisputes] = useState<DisputeRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [showForm, setShowForm] = useState<boolean>(false);
  const [category, setCategory] = useState<DisputeCategory>("damage");
  const [description, setDescription] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, tripId]);

  async function refresh(): Promise<void> {
    if (accessToken === null || tripId === undefined) {return;}
    setIsLoading(true);
    setError(null);
    try {
      const [t, d] = await Promise.all([
        tripsApi.detail(accessToken, tripId),
        disputesApi.listForTrip(accessToken, tripId),
      ]);
      setTrip(t);
      setDisputes(d.items);
    } catch (err: unknown) {
      setError(
        err instanceof ApiError ? err.problem.detail : "Couldn't load disputes.",
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function onFileDispute(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (accessToken === null || tripId === undefined) {return;}
    if (description.trim().length < 10) {
      toast.error("Description must be at least 10 characters.");
      return;
    }
    setIsSaving(true);
    try {
      await disputesApi.raise(accessToken, tripId, {
        category,
        description: description.trim(),
      });
      toast.success("Dispute filed. Admin review will follow.");
      setShowForm(false);
      setDescription("");
      await refresh();
    } catch (err: unknown) {
      toast.error(
        err instanceof ApiError ? err.problem.detail : "Couldn't file dispute.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  const hasOpenDispute = disputes?.some(
    (d) => d.status === "open" || d.status === "under_review",
  ) ?? false;
  const tripIsTerminal =
    trip !== null && (trip.status === "completed" || trip.status === "cancelled");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Trip disputes"
        description={
          trip !== null
            ? `${trip.pickup_district ?? "?"} → ${trip.drop_district ?? "?"} · Trip value ₹${trip.trip_value}`
            : "Loading…"
        }
        actions={
          <Link to="/disputes">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
              All disputes
            </Button>
          </Link>
        }
      />

      {error !== null ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {isLoading || trip === null || disputes === null ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {!tripIsTerminal ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Disputes can only be filed on completed or cancelled trips.
              </AlertDescription>
            </Alert>
          ) : null}

          {tripIsTerminal && !hasOpenDispute ? (
            <div className="flex justify-end">
              <Button
                onClick={() => { setShowForm((v) => !v); }}
                variant={showForm ? "outline" : "default"}
              >
                <Plus className="h-4 w-4" />
                {showForm ? "Cancel" : "File new dispute"}
              </Button>
            </div>
          ) : null}

          {showForm ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">File a dispute</CardTitle>
                <CardDescription>
                  Be specific -- include what happened, when, and any
                  receipts you can attach later via support.
                </CardDescription>
              </CardHeader>
              <form onSubmit={(e) => void onFileDispute(e)}>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="category">Category</Label>
                    <select
                      id="category"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      value={category}
                      onChange={(e) => { setCategory(e.target.value as DisputeCategory); }}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      rows={5}
                      minLength={10}
                      maxLength={2000}
                      required
                      value={description}
                      onChange={(e) => { setDescription(e.target.value); }}
                      placeholder="Describe the issue in detail…"
                    />
                  </div>
                </CardContent>
                <div className="flex justify-end gap-2 p-6 pt-0">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowForm(false); }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Filing…
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        File dispute
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          ) : null}

          {disputes.length === 0 ? (
            <Card>
              <CardHeader className="items-center text-center">
                <Scale className="h-10 w-10 text-muted-foreground" />
                <CardTitle>No disputes on this trip</CardTitle>
                <CardDescription>
                  You haven't raised any disputes on this trip.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <div className="space-y-3">
              {disputes.map((d) => (
                <DisputeCard key={d.id} d={d} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DisputeCard({ d }: { d: DisputeRead }): React.JSX.Element {
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
              STATUS_TONE[d.status],
            )}
          >
            {STATUS_LABEL[d.status]}
          </span>
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            {d.category.replace(/_/g, " ")}
          </span>
          <span className="text-xs text-muted-foreground">
            · raised {formatDate(d.raised_at)}
          </span>
        </div>
        <p className="whitespace-pre-wrap text-sm">{d.description}</p>
        {d.status === "resolved" || d.status === "rejected" ? (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {d.status === "resolved" ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-success" />
              ) : (
                <XCircle className="h-3.5 w-3.5 text-danger" />
              )}
              Admin resolution
              {d.resolved_at !== null ? ` · ${formatDate(d.resolved_at)}` : ""}
            </p>
            {d.resolution_action !== null ? (
              <p className="mt-1">
                <span className="font-medium">Action:</span>{" "}
                {d.resolution_action.replace(/_/g, " ")}
                {Number.parseFloat(d.refund_amount) > 0
                  ? ` · ₹${d.refund_amount}`
                  : ""}
              </p>
            ) : null}
            {d.resolution_notes !== null ? (
              <p className="mt-1 text-muted-foreground">{d.resolution_notes}</p>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
